import { createAsyncThunk } from '@reduxjs/toolkit';

import { wsMmSend } from '@skynet/data-ws-mm';
import { LoanCommands } from '@skynet/loans';

import type { AddLoanPayload } from './types';

// NOTE: plain `createAsyncThunk` (not `createAppAsyncThunk`) is used because this
// package must not depend on the app's RootState/AppDispatch. These thunks only
// `dispatch` a WS send and never call `getState`, so typed thunkApi is unnecessary;
// if state access is ever needed, switch to `.withTypes<{ state; dispatch }>()`.
//
// `wsMmSend` is not wrapped in try/catch: it's a plain, synchronous action
// creator (see `send` in `@skynet/data-ws-core`) that only builds a
// `{ type, meta, payload }` action — it cannot throw. The actual socket write
// happens later in the WS middleware, decoupled from this dispatch call.
//
// Because the thunk resolves immediately after dispatch (before any server ack),
// `.unwrap()` callers receive success even if the socket is down or the command
// fails — real outcomes arrive asynchronously via `actionOnMessage` in the slice.
export const addLoan = createAsyncThunk('loans/addLoan', (data: AddLoanPayload, thunkApi) => {
  const { paybackAmounts, ...rest } = data;
  const payload = {
    ...rest,
    basePaybackAmount: paybackAmounts.baseAmount,
    quotePaybackAmount: paybackAmounts.quoteAmount,
    residualPaybackAmount: paybackAmounts.residualAmount,
  };

  thunkApi.dispatch(
    wsMmSend({
      command: LoanCommands.AddLoan,
      ...payload,
    }),
  );

  return data;
});

export type UpdateLoanPayload = Partial<AddLoanPayload> & {
  loanID: number;
  basePaybackAmount?: number;
  quotePaybackAmount?: number;
  residualPaybackAmount?: number;
};

export const updateLoan = createAsyncThunk(
  'loans/updateLoan',
  (data: UpdateLoanPayload, thunkApi) => {
    const { paybackAmounts, ...rest } = data;
    const payload: Record<string, unknown> = { ...rest };

    if (paybackAmounts) {
      payload.basePaybackAmount = paybackAmounts.baseAmount;
      payload.quotePaybackAmount = paybackAmounts.quoteAmount;
      payload.residualPaybackAmount = paybackAmounts.residualAmount;
    }

    thunkApi.dispatch(
      wsMmSend({
        command: LoanCommands.UpdateLoan,
        ...payload,
      }),
    );

    return data;
  },
);
